import { useMemo } from "react";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { dayjs } from "@/core/utils/date";
import { formatDateTimeWithZone } from "@/core/utils/date";
import { formatMoneyCents } from "@/core/utils/money";
import { getCustomerNameOrPhone } from "@/customer/utils";
import { IReferral } from "@/referrals/referral";

interface ReferralActivity {
  message: React.ReactNode;
  event_date: string;
}

const ReferralActivity = ({ message, event_date }: ReferralActivity) => (
  <Stack gap="sm">
    {message}
    <Typography variant="subtitleW3" className="text-tertiary-text">
      {formatDateTimeWithZone(event_date)}
    </Typography>
  </Stack>
);

interface ReferralActivityListProps {
  referral: IReferral;
}

export const ReferralActivityList = ({
  referral,
}: ReferralActivityListProps) => {
  const activities = useMemo(
    () =>
      [...referral.payouts]
        .sort((a, b) => dayjs(b.created_at).diff(dayjs(a.created_at)))
        .map((payout) => ({
          id: payout.id,
          message: (
            <Typography variant="bodyW1">
              Referral payment made to{" "}
              <span className="text-lg font-semibold text-interactive-text">
                {getCustomerNameOrPhone(referral.advocate)}
              </span>{" "}
              of {formatMoneyCents(payout.amount)}
            </Typography>
          ),
          event_date: payout.created_at,
        })),
    [referral.payouts],
  );

  if (!activities.length) {
    return (
      <Typography
        variant="bodyW1"
        textColor="tertiary"
        className="mt-6 w-full text-center"
      >
        No payment history for this referral
      </Typography>
    );
  }

  return (
    <Stack gap="lg" className="mt-6">
      {activities.map((activity) => (
        <ReferralActivity key={activity.id} {...activity} />
      ))}
    </Stack>
  );
};
